import {
  ApplicationStatus,
  FamilyApplicationResp,
} from '@/apis/family_application/requests.ts'
import { DocumentIcon } from '@/app/common/icons/general'
import { ChatIcon } from '@/app/common/icons/general.tsx'
import {
  MarketplaceIcon,
  PaymentsIcon,
  ReimbursementIcon,
} from '@/app/common/icons/menu'
import { isValidRoute } from '@/app/services/ApplicationStatusAuthorization/application-status.utils.ts'
import {
  AssessmentsIcon,
  DashboardIcon,
  HandbookIcon,
  HelpCenterIcon,
  MyAccountIcon,
} from '@/common/icons/menu.tsx'
import { ProgramSettingsResp } from '@/storage/program_settings/requests.ts'
import { ReactElement } from 'react'
import { AppRoutePaths } from '../../routing/route_paths.ts'

export type MenuLink = {
  title: string
  icon: ReactElement
  route: string
  children?: { title: string; route: string }[]
}

export type LanguageKey = keyof typeof AppRoutePaths.external_lang_specific

// DON'T export MenuLinks
// Instead use the getMenuItems function to get the menu items based on the application status
const getMenuLinks = (
  helpCenterLink?: string | null,
  handbookLink?: string | null
): MenuLink[] => {
  const menuItems: MenuLink[] = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      route: AppRoutePaths.dashboard.index,
    },
    // {
    //   title: 'appeals',
    //   icon: <ArchiveIcon />,
    //   route: AppRoutePaths.appeals.index,
    // },
    /*  {
      title: 'notifications',
      icon: <NotificationsIcon />,
      route: AppRoutePaths.notifications.index,
    }, */
    {
      title: 'messages',
      icon: <ChatIcon />,
      route: AppRoutePaths.messages.index,
    },
    {
      title: 'document_menu',
      icon: <DocumentIcon />,
      route: AppRoutePaths.documents.index,
    },
    {
      title: 'assessments',
      icon: <AssessmentsIcon />,
      route: AppRoutePaths.assessments.index,
    },
    {
      title: 'pending_payments',
      icon: <PaymentsIcon />,
      route: AppRoutePaths.payments.index,
    },
    {
      title: 'pay_providers',
      icon: <PaymentsIcon />,
      route: AppRoutePaths.providers.index,
    },
    {
      title: 'reimbursements',
      icon: <ReimbursementIcon />,
      route: AppRoutePaths.reimbursements.new_reimbursement,
      children: [
        {
          title: 'new_reimbursement',
          route: AppRoutePaths.reimbursements.new_reimbursement,
        },
        {
          title: 'accounts',
          route: AppRoutePaths.reimbursements.accounts.index,
        },
      ],
    },
    {
      title: 'Marketplace',
      icon: <MarketplaceIcon />,
      route: AppRoutePaths.marketplace.index,
    },
    {
      title: 'transactions',
      icon: <PaymentsIcon />,
      route: AppRoutePaths.transactions.index,
    },
    ...(handbookLink
      ? [
          {
            title: 'handbook',
            icon: <HandbookIcon />,
            route: handbookLink,
          },
        ]
      : []),
    {
      title: 'my_account',
      icon: <MyAccountIcon />,
      route: AppRoutePaths.my_account.index,
    },
  ]

  // Only add help center if a valid link exists
  if (helpCenterLink) {
    menuItems.splice(-1, 0, {
      title: 'help_center',
      icon: <HelpCenterIcon />,
      route: helpCenterLink,
    })
  }

  return menuItems
}

export function getMenuItems(
  applicationsStatuses: ApplicationStatus[],
  programSettings: ProgramSettingsResp,
  familyApplication: FamilyApplicationResp,
  helpCenterLink?: string | null | undefined,
  handbookLink?: string | null | undefined
): readonly MenuLink[] {
  const results: Map<string, MenuLink> = new Map()

  applicationsStatuses.forEach((status) => {
    const menuLinks = getMenuLinks(helpCenterLink, handbookLink)
      .map((item) => {
        if (item.children) {
          const allowedPages = item.children.filter((subItem) =>
            isValidRoute({
              applicantStatus: status,
              pathname: subItem.route,
              programSettings,
            })
          )
          return allowedPages.length > 0
            ? { ...item, children: allowedPages }
            : null
        }
        return isValidRoute({
          applicantStatus: status,
          pathname: item.route,
          programSettings: programSettings,
        })
          ? item
          : null
      })
      .filter((item) => item !== null)

    menuLinks.forEach((menuLink) => {
      if (menuLink.route === AppRoutePaths.documents.index) {
        if (familyApplication.submitted_at) {
          results.set(menuLink.route, menuLink)
        }
      } else if (!results.has(menuLink.route)) {
        results.set(menuLink.route, menuLink)
      }
    })
  })
  return Array.from(results.values())
}
